package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.site.components.editor.ProjectQaSurface
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LShieldIcon
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute

@Page("/app/projects/{slug}/qa")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectQaPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val verifiedRoute = liveProjectRoute(slug)
    val authoritySubject = verifiedRoute?.authoritySubject(Session.organizations)
    if (verifiedRoute == null || authoritySubject == null) {
        // Two bare `?: return`s used to render the app shell with nothing inside it — no header, no
        // skeleton, no refusal — for the whole time the route was being verified. Every sibling
        // project page says it is loading; this one said nothing at all, which reads as a page that
        // failed to exist.
        Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
            PageHeader(
                t("app.projects.slug.qa.quality_assurance", "Quality assurance"),
                t("app.projects.slug.qa.find_and_fix_translation_risks_before", "Find and fix translation risks before they ship."),
            )
            LoadingState(rows = 7)
        }
        return
    }
    ProjectQaSurface(
        slug = slug,
        project = verifiedRoute.project,
        authority = ProjectAuthority.resolve(verifiedRoute.project, Session.organizations),
        authoritySubject = authoritySubject,
        mutationClaim = ProjectActionClaim.capture(
            verifiedRoute,
            Session.organizations,
            ProjectPrivilege.QA_MANAGEMENT,
        ),
        rulesAction = {
            // A button, like the header action on every sibling route in this lane. Drawn with
            // `LKeyValueRowLinkStyle` — a style built for key/value rows — it was a bare accent
            // text link with no border, no glyph and no 40px target, and at 390 it landed alone on
            // its own line reading as stray body text rather than as the page's one way into the
            // rules that produced everything below it.
            LLinkButton(
                t("app.projects.slug.qa.qa_rules", "QA rules"),
                "/app/projects/${verifiedRoute.project.slug}/settings/qa",
                variant = LButtonVariant.SECONDARY,
                leadingIcon = { LShieldIcon() },
            )
        },
        // A route push, not a fetch: a readiness outage must never be able to blank the issue list.
        onOpenProject = { ctx.router.tryRoutingTo("/app/projects/$slug") },
        initialLocale = ctx.route.params["locale"].orEmpty(),
        onOpenIssue = { issue ->
            ctx.router.tryRoutingTo(
                "/app/projects/$slug/translate/${encodeURIComponent(issue.locale)}" +
                    "?key=${encodeURIComponent(issue.keyId)}&search=${encodeURIComponent(issue.keyName)}",
            )
        },
    )
}

private external fun encodeURIComponent(value: String): String
